import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { SUPPORT_URL } from '../../app/config';
import { confirmContact, confirmPhone, signIn, type AuthResult } from '../../domain/authFlow';
import type { SignedContact, TelegramApi } from '../../telegram/webapp';
import { report } from '../../app/report';
import { MessageScreen, PhoneScreen, SplashScreen } from './screens';

interface Props {
  telegram: TelegramApi;
  /** Calls the login method with the current initData. */
  login: () => Promise<string>;
  /** Max: sends the signed phone from requestContact and resolves with the token. */
  confirm?: (contact: SignedContact) => Promise<string>;
  sleep?: (ms: number) => Promise<void>;
  children: (token: string) => ReactNode;
}

type State = { kind: 'checking' } | { kind: 'confirming' } | AuthResult;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Inside Telegram: runs the launch login (spec §5) and shows the matching screen until a token exists. */
export function AuthGate({ telegram, login, confirm, sleep = wait, children }: Props) {
  const [state, setState] = useState<State>({ kind: 'checking' });
  const started = useRef(false);

  const run = useCallback(async () => {
    setState({ kind: 'checking' });
    setState(await signIn({ login, sleep }));
  }, [login, sleep]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    telegram.ready();
    void run();
  }, [telegram, run]);

  const sharePhone = useCallback(async () => {
    const shared = await telegram.requestContact();
    if (!shared) return;
    setState({ kind: 'confirming' });
    if (shared === true) setState(await confirmPhone({ login, sleep }));
    else setState(confirm ? await confirmContact(() => confirm(shared), sleep) : { kind: 'failed' });
  }, [telegram, login, confirm, sleep]);

  const support = useCallback(() => telegram.openLink(SUPPORT_URL), [telegram]);

  // Every login outcome other than success or the expected phone screen is reported (roadmap P0-5).
  useEffect(() => {
    if (!['checking', 'confirming', 'signedIn', 'needPhone'].includes(state.kind)) report(`login_${state.kind}`);
  }, [state.kind]);

  switch (state.kind) {
    case 'signedIn':
      return <>{children(state.token)}</>;
    case 'checking':
      return <SplashScreen />;
    case 'needPhone':
    case 'confirming':
    case 'phoneNotConfirmed':
      return (
        <PhoneScreen
          messenger={telegram.name}
          busy={state.kind === 'confirming'}
          error={state.kind === 'phoneNotConfirmed' ? 'Не получилось подтвердить номер, попробуйте ещё раз' : undefined}
          onShare={sharePhone}
          onSupport={support}
        />
      );
    case 'expired':
      return (
        <MessageScreen
          title="Откройте приложение заново"
          text={`Данные ${telegram.name} устарели — приложение было открыто слишком долго. Закройте его и откройте снова.`}
        />
      );
    case 'invalidInitData':
      return (
        <MessageScreen
          title="Откройте приложение заново"
          text={`${telegram.name} не подтвердил данные входа. Закройте приложение и откройте его из бота ${telegram.messenger === 'max' ? 'PASS24' : '@pass24app_bot'}. Если не помогло — напишите в поддержку.`}
          onSupport={support}
        />
      );
    case 'unsupportedPhone':
      return <MessageScreen title="Не можем войти по этому номеру" text={state.message} onSupport={support} />;
    case 'rateLimited':
      return (
        <MessageScreen
          title="Слишком много попыток"
          text="Подождите минуту и попробуйте снова."
          onRetry={run}
        />
      );
    case 'offline':
      return <MessageScreen title="Нет связи" text="Проверьте интернет и попробуйте снова." onRetry={run} />;
    case 'failed':
      return (
        <MessageScreen
          title="Не получилось войти"
          text="Что-то пошло не так на нашей стороне. Попробуйте ещё раз или напишите в поддержку."
          onRetry={run}
          onSupport={support}
        />
      );
  }
}
